import { LucideIcon } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Shared header geometry (TES-1660 — Page-Header-Konsistenz).
 *
 * Die Titel-Zeile ist `flex items-center gap-3` mit einem `h-7 w-7`-Icon davor.
 * Damit die Subtitle exakt unter dem TITEL (nicht unter dem Icon) startet, muss ihr
 * linker Einzug = Icon-Breite + Gap sein. Diese Werte werden hier EINMAL definiert
 * und sowohl fuer das Icon als auch fuer den Subtitle-Einzug verwendet — kein
 * hartcodiertes `ml-10` mehr, das beim Aendern der Icon-Groesse stumm driftet.
 */
const ICON_BOX = "h-7 w-7"; // 28px — Industrial-DS Page-Icon
const TITLE_ROW_GAP = "gap-3"; // 12px zwischen Icon und Titel
/** Einzug der Subtitle = Icon-Breite (1.75rem) + gap-3 (0.75rem) = 2.5rem → ml-10. */
const SUBTITLE_INDENT = "ml-10";

interface PageHeaderProps {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Page title - can be string or translated ReactNode */
  title: ReactNode;
  /** Optional element to display after the title (e.g., OverdueBadge) */
  titleSuffix?: ReactNode;
  /** Page subtitle - can be string or translated ReactNode */
  subtitle?: ReactNode;
  /** Optional actions to display on the right side */
  actions?: ReactNode;
  /** Optional extra classes for the <h1> title (e.g. accent colour on dashboards). */
  titleClassName?: string;
  /** Optional className for the container */
  className?: string;
}

/**
 * Standardized page header component for all pages.
 *
 * Implements the consistent header pattern (TES-1660 — eine Quelle fuer die
 * vertikale Titel-Position auf JEDER Seite):
 * - Container: `flex-col sm:flex-row sm:items-start sm:justify-between` — der
 *   Titel-Block steht IMMER ganz oben links, Actions rechts. (RoleDashboardHeader
 *   rendert ueber genau diese Struktur, damit das Dashboard nicht anders sitzt.)
 * - Icon: `h-7 w-7 text-primary shrink-0`
 * - Title: Industrial-DS H1 — `.text-h1` (Space Grotesk via `.font-display`, 32px/700,
 *   tracking -0.02em). Loest das alte `text-2xl font-bold` ab (DS-Reskin TES-961 §5).
 * - Subtitle: `text-muted-foreground mt-1` mit Einzug = Icon-Breite + Gap
 *   (SUBTITLE_INDENT) — startet damit buendig unter dem Titel, nicht unter dem Icon.
 *
 * @see .lovable/memory/style/admin-page-header-standard.md
 */
export function PageHeader({
  icon: Icon,
  title,
  titleSuffix,
  subtitle,
  actions,
  titleClassName = "",
  className = "",
}: PageHeaderProps) {
  return (
    <div
      className={cn("flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4", className)}
    >
      <div className="min-w-0">
        {/* Icon bleibt IMMER neben der ersten Titel-Zeile (TES-1660): das Icon ist
            `shrink-0` und der Titel-Block daneben nimmt die Restbreite + bricht NUR
            seinen eigenen Text um (min-w-0). Ohne diese Trennung wickelt ein langer
            Titel (z.B. Dashboard "Hier ist Ihre Übersicht für heute") auf schmalen
            Viewports das Icon auf eine eigene Zeile und der Titel rutscht 40px tiefer
            als auf Seiten mit kurzem Titel — genau der vom Owner gemeldete Versatz. */}
        <div className={cn("flex items-center", TITLE_ROW_GAP)}>
          <Icon className={cn(ICON_BOX, "text-primary shrink-0")} />
          <div className={cn("flex min-w-0 flex-1 items-center flex-wrap", TITLE_ROW_GAP)}>
            <h1 className={cn("text-h1 font-display", titleClassName)} data-testid="page-title">
              {title}
            </h1>
            {titleSuffix && <div className="shrink-0">{titleSuffix}</div>}
          </div>
        </div>
        {subtitle && (
          <p className={cn("text-muted-foreground mt-1", SUBTITLE_INDENT)}>{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}
